import Card from "@/UI/Card";
import { useUpdateDealMutation, useGetDealQuery } from "@/redux/rtk/features/CRM/deal/dealApi";
import { useParams } from "react-router-dom";
import { CheckCircleOutlined } from "@ant-design/icons";
import { Modal, Input, Button, Select, message } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

export default function DealStageChanger({ dealStages, currentDeal, onStageChange }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [updateDeal, { isLoading: isUpdating }] = useUpdateDealMutation();
  const { refetch } = useGetDealQuery(id);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [stageNotes, setStageNotes] = useState("");

  const currentStageIndex = dealStages?.findIndex(
    (stage) => stage.id === currentDeal?.dealStageId
  ) ?? -1;

  const handleReviewStatusChange = async (value) => {
    if (isUpdating) return;
    try {
      await updateDeal({ id, values: { reviewStatus: value } }).unwrap();
      if (onStageChange) {
        onStageChange();
      } else {
        refetch();
      }
    } catch (error) {
      console.error("Failed to update review status:", error);
    }
  };

  const handleStageClick = (stageId) => {
    if (isUpdating) return;
    if (stageId === currentDeal?.dealStageId) return;

    const targetStageIndex = dealStages?.findIndex((s) => s.id === stageId) ?? -1;
    const isMovingForward = targetStageIndex > currentStageIndex;

    if (isMovingForward) {
      const targetStage = dealStages[targetStageIndex];

      // 1. Enforce sequential forward movement (only one step at a time)
      if (targetStage?.stageName !== "Closed Lost" && targetStageIndex > currentStageIndex + 1) {
        message.warning(t("deal.validate.sequential"));
        return;
      }

      const attachments = currentDeal?.attachments || [];

      const checkKeyword = (keywords) => {
        return attachments.some((att) => {
          const name = (att.attachmentName || "").toLowerCase();
          const type = (att.attachmentType || "").toLowerCase();
          return keywords.some((kw) => name.includes(kw) || type.includes(kw));
        });
      };

      if (targetStage?.stageName === "Paper Submitted") {
        const hasPassport = checkKeyword(["passport", "جواز"]);
        const hasCertificate = checkKeyword(["certificate", "شهادة", "transcript", "بيان", "diploma", "وثيقة"]);
        if (!hasPassport || !hasCertificate) {
          message.warning(t("deal.validate.papersRequired"));
          return;
        }
      }

      if (targetStage?.stageName === "Conditional Acceptance Sent") {
        const hasConditional = checkKeyword(["conditional", "قبول مشروط", "القبول المشروط", "قبول_مشروط", "خطاب قبول مشروط", "خطاب_قبول_مشروط"]);
        if (!hasConditional) {
          message.warning(t("deal.validate.conditionalRequired"));
          return;
        }
      }

      if (targetStage?.stageName === "Payment on process") {
        const hasPayment = checkKeyword(["payment", "إيصال دفع", "إيصال الدفع", "دفع"]);
        if (!hasPayment) {
          message.warning(t("deal.validate.paymentRequired"));
          return;
        }
      }

      if (targetStage?.stageName === "Final letter sent") {
        const hasFinalLetter = checkKeyword(["final", "final letter", "final_letter", "خطاب نهائي", "نهائي"]);
        if (!hasFinalLetter) {
          message.warning(t("deal.validate.finalLetterRequired"));
          return;
        }
      }

      if (targetStage?.stageName === "Closed Won") {
        const hasStudentLetter = checkKeyword(["student letter", "student_letter", "خطاب الطالب", "خطاب_الطالب"]);
        if (!hasStudentLetter) {
          message.warning(t("deal.validate.studentLetterRequired"));
          return;
        }
      }
    }

    setSelectedStageId(stageId);
    setStageNotes("");
    setModalVisible(true);
  };

  const handleStageChange = async () => {
    if (isUpdating || !selectedStageId) return;

    try {
      const updateData = { dealStageId: selectedStageId };
      // Save notes if provided, otherwise continue without notes
      if (stageNotes.trim()) {
        updateData.stageNotes = stageNotes.trim();
      }

      await updateDeal({ id, values: updateData }).unwrap();
      setModalVisible(false);
      setSelectedStageId(null);
      setStageNotes("");

      if (onStageChange) {
        onStageChange();
      } else {
        refetch();
      }
    } catch (error) {
      console.error("Failed to update deal stage:", error);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedStageId(null);
    setStageNotes("");
  };

  const progressWidth = dealStages?.length
    ? ((currentStageIndex + 1) / dealStages.length) * 100
    : 0;

  return (
    <Card className="mb-4 border-none shadow-sm bg-white dark:bg-gray-800">
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("deal.dealStages") || "Deal Stages"}
          </h3>
          <div className="flex items-center gap-4">
            <Select
              value={currentDeal?.reviewStatus || "pending_review"}
              onChange={handleReviewStatusChange}
              loading={isUpdating}
              style={{ width: 150 }}
              options={[
                { value: 'pending_review', label: t("deal.pendingReview", "Pending Review") },
                { value: 'reviewed', label: t("deal.reviewed", "Reviewed") }
              ]}
            />
            {currentDeal?.probability !== undefined && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t("deal.probability")}:</span>
                <span className="text-base font-semibold text-blue-600 dark:text-blue-400">
                  {currentDeal.probability}%
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Stages Progress Stepper */}
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-[80px] left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700 z-0">
            <div
              className="h-full bg-blue-500 transition-all duration-500 ease-in-out"
              style={{
                width: `${progressWidth}%`
              }}
            />
          </div>

          {/* Stages */}
          <div className="relative">
            {/* Container للدوائر - كلها على سطر واحد */}
            <div className="flex justify-between items-center relative z-10" style={{ paddingTop: '60px', paddingBottom: '60px' }}>
              {dealStages?.map((stage, index) => {
                const isActive = index === currentStageIndex;
                const isCompleted = index < currentStageIndex;
                const isClickable = !isUpdating;

                return (
                  <div
                    key={stage.id}
                    className="flex-1 flex flex-col items-center relative"
                    style={{ minWidth: '120px' }}
                  >
                    {/* Step Circle - كل الدوائر على نفس المستوى */}
                    <button
                      onClick={() => isClickable && handleStageClick(stage.id)}
                      disabled={!isClickable}
                      className={`
                        relative w-12 h-12 rounded-full flex items-center justify-center
                        transition-all duration-300 ease-in-out
                        shadow-md hover:shadow-lg
                        ${isActive
                          ? stage.stageName === "Closed Won"
                            ? "bg-green-500 text-white scale-110 ring-4 ring-green-100 dark:ring-green-900/30"
                            : stage.stageName === "Closed Lost"
                              ? "bg-red-500 text-white scale-110 ring-4 ring-red-100 dark:ring-red-900/30"
                              : "bg-blue-500 text-white scale-110 ring-4 ring-blue-100 dark:ring-blue-900/30"
                          : isCompleted
                            ? "bg-[#8ba88b] text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                        }
                        ${isClickable && !isActive ? "hover:scale-105 hover:bg-gray-300 dark:hover:bg-gray-600" : ""}
                        ${isUpdating ? "opacity-50 cursor-wait" : isClickable ? "cursor-pointer" : "cursor-not-allowed"}
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircleOutlined className="text-lg" />
                      ) : (
                        <span className="text-sm font-semibold">{index + 1}</span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Container للأسماء - متناوبة فوق وتحت */}
            <div className="absolute top-0 left-0 right-0 flex justify-between">
              {dealStages?.map((stage, index) => {
                const isActive = index === currentStageIndex;
                const isClickable = !isUpdating;
                const isEven = index % 2 === 0; // true for even (0, 2, 4...) = فوق, false for odd (1, 3, 5...) = تحت

                return (
                  <div
                    key={`name-${stage.id}`}
                    className="flex-1 flex flex-col items-center relative"
                    style={{ minWidth: '120px' }}
                  >
                    {isEven ? (
                      // الأسماء الزوجية (0, 2, 4...) - فوق الدائرة
                      <button
                        onClick={() => isClickable && handleStageClick(stage.id)}
                        disabled={!isClickable}
                        className={`
                          text-sm font-medium text-center transition-all duration-200
                          ${isActive
                            ? stage.stageName === "Closed Won"
                              ? "text-green-600 dark:text-green-400 font-semibold"
                              : stage.stageName === "Closed Lost"
                                ? "text-red-600 dark:text-red-400 font-semibold"
                                : "text-blue-600 dark:text-blue-400 font-semibold"
                            : "text-gray-600 dark:text-gray-400"
                          }
                          ${isClickable ? "cursor-pointer hover:text-blue-500 dark:hover:text-blue-400" : "cursor-not-allowed"}
                          ${isUpdating ? "opacity-50" : ""}
                        `}
                        style={{ maxWidth: '120px' }}
                      >
                        {t(`deal.stages.${stage.stageName}`) || stage.stageName}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {/* Container للأسماء تحت الدائرة */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-between">
              {dealStages?.map((stage, index) => {
                const isActive = index === currentStageIndex;
                const isClickable = !isUpdating;
                const isEven = index % 2 === 0;

                return (
                  <div
                    key={`name-bottom-${stage.id}`}
                    className="flex-1 flex flex-col items-center relative"
                    style={{ minWidth: '120px' }}
                  >
                    {!isEven ? (
                      // الأسماء الفردية (1, 3, 5...) - تحت الدائرة
                      <button
                        onClick={() => isClickable && handleStageClick(stage.id)}
                        disabled={!isClickable}
                        className={`
                          text-sm font-medium text-center transition-all duration-200
                          ${isActive
                            ? stage.stageName === "Closed Won"
                              ? "text-green-600 dark:text-green-400 font-semibold"
                              : stage.stageName === "Closed Lost"
                                ? "text-red-600 dark:text-red-400 font-semibold"
                                : "text-blue-600 dark:text-blue-400 font-semibold"
                            : "text-gray-600 dark:text-gray-400"
                          }
                          ${isClickable ? "cursor-pointer hover:text-blue-500 dark:hover:text-blue-400" : "cursor-not-allowed"}
                          ${isUpdating ? "opacity-50" : ""}
                        `}
                        style={{ maxWidth: '120px' }}
                      >
                        {t(`deal.stages.${stage.stageName}`) || stage.stageName}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Stage Change Notes */}
      <Modal
        title={t("deal.changeStageTitle")}
        open={modalVisible}
        onCancel={handleModalCancel}
        footer={[
          <Button key="cancel" onClick={handleModalCancel}>
            {t("common.cancel")}
          </Button>,
          <Button key="continue" type="primary" onClick={handleStageChange} loading={isUpdating}>
            {t("deal.continue")}
          </Button>,
        ]}
        width={600}
      >
        <div className="space-y-4">
          <div>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              {t("deal.changeStageDescription")}
            </p>
            <TextArea
              rows={4}
              placeholder={t("deal.changeStagePlaceholder")}
              value={stageNotes}
              onChange={(e) => setStageNotes(e.target.value)}
              maxLength={500}
              showCount
              className="mb-4"
            />
          </div>
        </div>
      </Modal>
    </Card>
  );
}
